import { formatDuration, formatMonthYear, formatNumber, formatPercent } from "@/lib/format";
import { formatMoney, minorToMajor } from "@/lib/money";

export type ChartUnit = "currency" | "count" | "percent" | "minutes";

/** One figure in the user's locale. Percent values arrive as 0..100, minutes as whole minutes. */
export function formatChartValue(value: number, unit: ChartUnit, locale: string, currency?: string): string {
  switch (unit) {
    case "currency":
      return formatMoney(minorToMajor(value), true, currency);
    case "percent":
      return formatPercent(value / 100, locale);
    case "minutes":
      return formatDuration(value, locale);
    default:
      return formatNumber(value, locale);
  }
}

const MONTH_PERIOD = /^\d{4}-\d{2}$/;

/** "2026-01" as "Jan 26" / "janv. 26" in the user's language; other period shapes stay as sent. */
export function formatPeriodLabel(period: string, locale: string): string {
  if (!MONTH_PERIOD.test(period)) return period;
  return formatMonthYear(period, locale, "short");
}

/** Axis ticks: whole numbers, so the axis stays narrow. */
export function formatAxisValue(value: number, unit: ChartUnit, locale: string, currency?: string): string {
  switch (unit) {
    case "currency":
      return formatMoney(Math.round(minorToMajor(value)), true, currency).replace(/\.00$/, "");
    case "percent":
      return formatPercent(value / 100, locale, 0);
    case "minutes":
      return formatDuration(value, locale);
    default:
      return formatNumber(value, locale, { maximumFractionDigits: 1 });
  }
}

/** A Y axis wide enough for its longest tick label (about 7px per character at the axis font size). */
export function axisWidthFor(labels: string[]): number {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.min(112, Math.max(40, longest * 7 + 12));
}
